import type { FC } from "react";
import { useState, useEffect } from "react";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import { RefreshProvider } from "../contexts/RefreshContext";
import { useAuth } from "../contexts/AuthContext";
import {
  DashboardHeader,
  RevenueCards,
  SalesChart,
  SalesLeaderboard,
  LatestTransactions,
  // BonusProgressCard,
  // AcquisitionOverview, // Temporarily disabled
} from "../components/dashboard";
import { getWorkerById, getWorkerIdForUser } from "../services/workers";
import { useAdminAccess } from "../hooks/useAdminAccess";
import type { TeamSelection } from "../components/dashboard/TeamSelector";

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

const DashboardPage: FC = function () {
  // State for both selectors
  const [viewMode, setViewMode] = useState<"all" | "individual">("all");
  const [selectedOption, setSelectedOption] = useState<
    "today" | "yesterday" | "custom"
  >("today");
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(),
    endDate: new Date(),
  });
  const [isAdminRole, setIsAdminRole] = useState(false);
  const [teamSelection, setTeamSelection] = useState<TeamSelection>({
    type: "individual",
  });

  const { user } = useAuth();
  const { hasAccess, hasOwnerAccess, roleId } = useAdminAccess();

  // Set default team selection based on user role
  useEffect(() => {
    if (hasOwnerAccess || (roleId && roleId <= 2)) {
      setTeamSelection({ type: "all" });
    } else if (roleId === 3) {
      setTeamSelection({ type: "all_my_teams" }); // Team Leads start with all their teams combined
    } else {
      setTeamSelection({ type: "individual" });
    }
  }, [hasAccess, hasOwnerAccess, roleId]);

  // Check if user is admin
  useEffect(() => {
    const checkUserRole = async () => {
      if (user) {
        try {
          const workerId = await getWorkerIdForUser(user.id);
          const workerData = await getWorkerById(workerId);

          if (workerData) {
            setIsAdminRole(workerData.role_id <= 3);
          }
        } catch (error) {
          console.error("Error checking user role:", error);
        }
      }
    };

    checkUserRole();
  }, [user]);

  return (
    <NavbarSidebarLayout pageTitle="Übersicht">
      <div className="px-2 pt-8">
        <DashboardHeader
          viewMode={viewMode}
          setViewMode={setViewMode}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          dateRange={dateRange}
          setDateRange={setDateRange}
          isAdminRole={isAdminRole}
        />
        <RefreshProvider>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-3">
            <div className="lg:col-span-2">
              <RevenueCards
                viewMode={viewMode}
                isAdminRole={isAdminRole}
                roleId={roleId}
              />
              <SalesChart
                teamSelection={teamSelection}
                onTeamSelectionChange={setTeamSelection}
              />
            </div>
            <div className="lg:col-span-1">
              {/* <BonusProgressCard /> */}
              <SalesLeaderboard
                selectedOption={selectedOption}
                dateRange={dateRange}
                isAdminRole={isAdminRole}
                teamSelection={teamSelection}
                setSelectedOption={setSelectedOption}
                setDateRange={setDateRange}
              />
            </div>
          </div>
        </RefreshProvider>
        <div className="my-6">
          <LatestTransactions />
        </div>
        {/* Temporarily disabled
        <div className="my-6">
          <AcquisitionOverview />
        </div>
        */}
      </div>
    </NavbarSidebarLayout>
  );
};

export default DashboardPage;
